<template>
  <form @submit.prevent="submit">
    <naj-box
      v-if="isCaseTypeIsReconciliationPlatformEnabled"
      :title="local.lawsuitRequest.reconciliationInfo.title"
    >
      <v-row>
        <v-col
          lg="6"
          cols="6"
        >
          <naj-input
            :type-id="formFieldType.radioButton"
            :value.sync="caseData.isReconciledBefore"
            :label="local.lawsuitRequest.reconciliationInfo.reconciliationPartiesStatusBefore"
            :items="yesOrNo.map(c=> ({...c,id:Boolean(c.id)}))"
            item-value="id"
            item-text="name"
            :disabled="isViewMode"
            validates="required"
            @change="isReconciledBeforeChanged"
          ></naj-input>
        </v-col>
        <v-col
          v-if="!isNaN(caseData.isReconciledBefore) && !caseData.isReconciledBefore"
          cols="6"
        >
          <naj-input
            :type-id="formFieldType.radioButton"
            :value.sync="caseData.intentionForReconciliation"
            :label="local.lawsuitRequest.reconciliationInfo.intentionForReconciliation"
            :items="yesOrNo.map(c=> ({...c,id:Boolean(c.id)}))"
            item-value="id"
            item-text="name"
            :disabled="isViewMode"
            @change="intentionForReconciliationChanged"
          ></naj-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-if="caseData.isReconciledBefore"
          cols="12"
          lg="4"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="caseData.reconciliationLocationId"
            :label="local.lawsuitRequest.reconciliationInfo.whenReconciliationOccured"
            :items="reconciliationLocations"
            item-value="id"
            item-text="name"
            validates="required"
            :disabled="isViewMode"
            @change="reconciliationLocationIdChanged"
          ></naj-input>
        </v-col>
        <v-col
          v-if="renderReconciliationRequestCode"
          cols="8"
          lg="4"
        >
          <naj-input
            v-mask="'01-##########'"
            :type-id="formFieldType.text"
            :value.sync="caseData.reconciliationRequestCode"
            :label="local.lawsuitRequest.reconciliationInfo.note"
            :disabled="isViewMode || isCheckingReconciliationRequestCode"
            :validates="`required|isVerified:${!!(caseData.reconciliation || {}).requestID}`"
            @input="onChangeReconciliationCode"
          ></naj-input>
        </v-col>
        <v-col
          v-if="isStatusFromTaradhiReconciliation && !(caseData.reconciliation || {}).requestID"
          cols="2"
          lg="2"
          class="d-flex align-center"
        >
          <naj-button
            square
            inputs-alignment
            :loading="isCheckingReconciliationRequestCode"
            :disabled="isViewMode"
            @click="validateReconciliationRequestCode(false)"
          >
            {{ local.common.check }}
          </naj-button>
        </v-col>

        <v-col
          v-if="isStatusFromTaradhiReconciliation
            && (caseData.reconciliation || {}).requestID"
          class="align-self-center the-rank"
        >
          {{ (caseData.reconciliation || {}).status }}
        </v-col>
        <v-col
          v-if="isStatusFromTaradhiReconciliation && ((caseData.reconciliation || {}).attachmentList || []).length > 0"
          class="align-self-center the-rank"
        >
          <naj-button
            outlined
            @click="openReconciliationAttachments(caseData)"
          >
            {{ local.lawsuitRequest.reconciliationInfo.reconciliationFile }}
          </naj-button>
        </v-col>
      </v-row>
    </naj-box>

    <reconciliation-attachments-modal
      :case-model="selectedCase"
      :modal-visibility="showReconciliationAttachmentsModal"
      @modalClose="closeReconciliationAttachmentsModal"
    ></reconciliation-attachments-modal>

    <v-col
      v-for="form in caseData.forms.filter(f=>!!flatDynamicFormsByName[f.formName])"
      :key="form.formName"
      cols="12"
    >
      <naj-dynamic-form
        :form-model="form"
        :form-fields="flatDynamicFormsByName[form.formName].fields"
        :custom-component-name="flatDynamicFormsByName[form.formName].customComponentName"
        :required-data="requiredDataForDynamicForm"
        :view-mode="isViewMode"
        @change="formChanged('naj-dynamic-form')"
        @externalEffectEvent="externalEffectEventHandler"
      ></naj-dynamic-form>
      <case-requests
        :form-model="form"
        :case-type-id="caseData.caseTypeId"
        :required-data="requiredDataForDynamicForm"
        :is-view-mode="isViewMode"
        @change="formChanged('case-requests')"
      ></case-requests>
      <terminations-lawsuit
        v-if="showMinorConcerned"
        :form-model="caseData"
        :is-view-mode="isViewMode"
      >
      </terminations-lawsuit>
    </v-col>

    <court-details
      :dynamic-form-changes="formModelTrackedChanges"
      @change="onCourtChange"
    ></court-details>

    <similar-case
      v-if="allowedCaseTypes"
      :form-model="lawsuitRequest"
      :view-mode="isViewMode"
    ></similar-case>

    <default-lawsuit-details
      :proceeding-info="caseData"
      :is-case-subject-text-editable="isCaseSubjectTextEditable"
      :is-case-demands-text-editable="isCaseDemandsTextEditable"
      :case-has-form-id="caseHasFormId"
      @change="$set(caseData, 'caseHasChanges', true)"
    ></default-lawsuit-details>

    <v-row>
      <v-col
        cols="12"
        class="text-left"
      >
        <naj-button
          outlined
          @click="stepBack"
        >
          {{ local.common.back }}
        </naj-button>
        <naj-button type="submit">
          {{ nextButtonLabel }}
        </naj-button>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import TerminationsLawsuit from './terminations-lawsuit/index.vue';
import DefaultLawsuitDetails from './default-lawsuit-details/index';
import ReconciliationAttachmentsModal from './reconciliation-attachments-modal/index';
import CourtDetails from './court-details/index';
import SimilarCase from './similar-case/index.vue';
import CaseRequests from '@/modules/global/components/dynamic-form/custom-components/case-requests/index';
import dataSourceConfig from '../../helpers/dynamic-form-configs';
import { isPerson, isPersonalLawsuit, isOrganization, isTerminationsLawsuit, isOrganizationOwner } from '../../helpers/utils';
import { scrollIntoInValidInput } from '@/helpers/utils';
import { createNamespacedHelpers } from 'vuex';
import { reconciliationLocationEnum, reconciliationResultEnum, attachmentType, caseTypeEnum } from '@/helpers/enum';

const { mapActions, mapState, mapGetters } = createNamespacedHelpers('LawsuitModule');
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers('LookupsModule');
const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');

export default {
  inject: ['mode'],
  components: {
    DefaultLawsuitDetails,
    CourtDetails,
    CaseRequests,
    ReconciliationAttachmentsModal,
    SimilarCase,
    TerminationsLawsuit,
  },
  data() {
    return {
      isTerminationsLawsuit,
      formModelTrackedChanges: {},
      isPersonalLawsuit,
      debounceTimeout: null,
      isCheckingReconciliationRequestCode: false,
      reconciliationLocationEnum,
      selectedCase: null,
      reconciliationResultEnum,
      showReconciliationAttachmentsModal: false,
      firstProsecutorIdentityNumber: null,
      firstProsecutorCommercialRegistrationNumber: null,
      firstProsecutorUnifiedNumber: null,
    };
  },
  watch: {
    'prosecutors': {
      handler() {
        this.setProsecutor();
        this.validateReconciliationRequestCode(true);
      },
      deep: true,
    },
    'caseData.caseClassificationIdNew'(value) {
      this.getUserCaseCategories(value);
    },
    'caseData.caseCategoryId'(value) {
      this.getUserCaseTypes(value);
      this.getCaseTypesByCategory(value);
    },
    selectedCaseType(value) {
      const subjectFormId = value && value.subjectFormId;
      subjectFormId && this.getNeededDynamicForm(subjectFormId);
    },
  },
  created() {
    this.getReconciliationLocations();
    const subjectFormId = this.selectedCaseType && this.selectedCaseType.subjectFormId;
    subjectFormId && this.getNeededDynamicForm(subjectFormId);
    setTimeout(() => {
      this.setProsecutor();
      if (!(this.caseData.reconciliation || {}).requestID)
        this.validateReconciliationRequestCode(true);
    }, 500);
  },
  computed: {
    ...mapState({
      lawsuitRequest: state => state.lawsuitRequest,
      caseData: state => state.lawsuitRequest.case,
      request: state => state.submittedRequestInfo,
      flatDynamicFormsByName: state => state.flatDynamicFormsByName,
    }),
    ...mapGetters([
      'isViewMode',
      'isNewMode',
      'isEditMode',
      'selectedCaseType',
      'prosecutors',
    ]),
    ...mapLUTState(['caseTypesByCategory', 'reconciliationLocations', 'yesOrNo']),
    nextButtonLabel() {
      if (this.isViewMode || (this.isEditMode && !this.caseData.caseHasChanges)) {
        return this.local.common.next;
      }
      if (this.isNewMode || (this.isEditMode && this.caseData.caseHasChanges)) {
        return this.local.common.saveAndContinue;
      }
      return '';
    },
    renderReconciliationRequestCode() {
      return (this.caseData.isReconciledBefore
        && this.caseData.reconciliationLocationId == this.reconciliationLocationEnum.reconciliationCenterForTaradhi);
    },
    isStatusFromTaradhiReconciliation() {
      return this.caseData.reconciliationRequestCode
        && this.caseData.isReconciledBefore
        && this.caseData.reconciliationLocationId == reconciliationLocationEnum.reconciliationCenterForTaradhi;
    },
    isCaseSubjectTextEditable() {
      const caseTypes = this.caseTypesByCategory[this.caseData.caseCategoryId];
      const caseType = (caseTypes || []).find(caseType => caseType.id == this.caseData.caseTypeId);
      if (caseType && caseType.allowCaseSubjectTextModifications)
        return true;
      return false;
    },
    isCaseDemandsTextEditable() {
      const caseTypes = this.caseTypesByCategory[this.caseData.caseCategoryId];
      const caseType = (caseTypes || []).find(caseType => caseType.id == this.caseData.caseTypeId);
      if (caseType && caseType.allowCaseDemandsTextModifications)
        return true;
      return false;
    },
    caseHasFormId() {
      const caseTypes = this.caseTypesByCategory[this.caseData.caseCategoryId];
      const caseType = (caseTypes || []).find(caseType => caseType.id == this.caseData.caseTypeId);
      if (caseType && caseType.subjectFormId)
        return true;
      return false;
    },
    showMinorConcerned() {
      return this.isTerminationsLawsuit(this.caseData);
    },
    isCaseTypeIsReconciliationPlatformEnabled() {
      return (this.selectedCaseType || {}).isReconciliationPlatformEnabled || (this.selectedCaseType || {}).isReconciliationPlatformFeasible;
    },
    requiredDataForDynamicForm() {
      return dataSourceConfig.reduce((pv, cv) => {
        const data = cv.executer(this.lawsuitRequest);
        if (cv.inRoot) return {
          ...pv,
          ...data,
        };
        pv[cv.id] = data;
        return pv;
      }, {});
    },
    allowedCaseTypes() {
      const applicableCaseTypes = [
        caseTypeEnum.financialClaim_feesOfLawyerOrAgent,
        caseTypeEnum.judicialRequests_feesOfLawyerOrAgent,
        caseTypeEnum.financial_feesOfLawyerOrAgent,
        caseTypeEnum.financialRights_feesOfLawyerOrAgent,
        caseTypeEnum.generalClassification_feesOfLawyerOrAgent,
        caseTypeEnum.nonFormalDisputes_feesOfLawyerOrAgent,
      ];

      return applicableCaseTypes.some(ct => ct == this.caseData.caseTypeId);
    },
  },
  methods: {
    ...mapActions({
      save: 'stepCaseSubjectNext',
      stepBack: 'stepBack',
      addCaseTypeDynamicFormModel: 'addCaseTypeDynamicFormModel',
      updateLawsuitDynamicForms: 'updateLawsuitDynamicForms',
      resetRelatedCases: 'resetRelatedCases',
    }),
    ...mapGlobalActions(['getDynamicForm', 'handleAttachments']),
    ...mapSysActions(['validateReconciliationCode']),
    ...mapLUTActions(['getUserCaseTypes', 'getUserCaseCategories', 'getReconciliationLocations', 'getCaseTypesByCategory']),
    submit() {
      this.$validator.validateAll()
        .then((isValid) => {
          if (isValid) {
            this.save();
          } else {
            scrollIntoInValidInput(this.errors);
          }
        });
    },
    setProsecutor() {
      const prosecutors = Object.values(this.prosecutors);
      if (prosecutors.length > 0) {
        if (isPerson(prosecutors[0]))
          this.firstProsecutorIdentityNumber = prosecutors[0].socialId;
        else if (isOrganization(prosecutors[0]) || isOrganizationOwner(prosecutors[0])) {
          this.firstProsecutorIdentityNumber = prosecutors[0].socialId;
          this.firstProsecutorCommercialRegistrationNumber = prosecutors[0].commercialRegistrationNumber;
          this.firstProsecutorUnifiedNumber = prosecutors[0].unifiedNumber;
        }
        else {
          this.firstProsecutorCommercialRegistrationNumber = prosecutors[0].commercialRegistrationNumber;
          this.firstProsecutorUnifiedNumber = prosecutors[0].unifiedNumber;
        }
      }
    },
    openReconciliationAttachments(caseData) {
      this.selectedCase = { ...caseData };
      this.showReconciliationAttachmentsModal = true;
    },
    closeReconciliationAttachmentsModal() {
      this.showReconciliationAttachmentsModal = false;
      this.selectedCase = null;
    },
    isReconciledBeforeChanged() {
      this.$set(this.caseData, 'reconciliationLocationId', null);
      this.$set(this.caseData, 'reconciliationRequestCode', null);
      this.$set((this.caseData.reconciliation || {}), 'requestID', null);
      this.$set((this.caseData.reconciliation || {}), 'attachmentList', []);
      this.isCheckingReconciliationRequestCode = false;
      this.formChanged('isReconciledBeforeChanged');
    },
    reconciliationLocationIdChanged() {
      this.$set(this.caseData, 'reconciliationRequestCode', null);
      this.$set((this.caseData.reconciliation || {}), 'requestID', null);
      this.$set((this.caseData.reconciliation || {}), 'attachmentList', []);
      this.isCheckingReconciliationRequestCode = false;
      this.formChanged('reconciliationLocationIdChanged');
    },
    intentionForReconciliationChanged() {
      this.formChanged('intentionForReconciliationChanged');
    },
    onChangeReconciliationCode() {
      this.$set(this.caseData, 'reconciliation', {});
      this.formChanged('onChangeReconciliationCode');
    },
    getNeededDynamicForm(formId, trial = 0) {
      this.getDynamicForm({
        formId,
        withoutParse: true,
      })
        .then((form) => {
          this.updateLawsuitDynamicForms(form);
          if (!this.caseData.forms.find(f => f.formName === form.name)) {
            this.addCaseTypeDynamicFormModel({
              formId,
              formName: form.name,
              caseTypeId: this.caseData.caseTypeId,
            });
          }
        })
        .catch((err) => {
          if (trial < 2)
            this.getNeededDynamicForm(formId, trial + 1);
          else
            throw err;
        });
    },
    formChanged(changeSource) {
      if (!this.caseData.caseHasChanges) this.$set(this.caseData, 'caseHasChanges', true);
      this.updateTemplates();
    },
    externalEffectEventHandler({ name, value }) {
      this.$set(this.formModelTrackedChanges, name, value);
    },
    updateTemplates() {
      if ((this.selectedCaseType || {}).subjectTemplate) {
        this.$set(this.caseData, 'caseSubject', this.handlebars(this.selectedCaseType.subjectTemplate, {
          ...this.requiredDataForDynamicForm,
          ...(this.caseData.forms[0]),
        }));
      }
      if ((this.selectedCaseType || {}).requestTemplate) {
        this.$set(this.caseData, 'caseDemands', this.handlebars(this.selectedCaseType.requestTemplate, {
          ...this.requiredDataForDynamicForm,
          ...(this.caseData.forms[0]),
        }));
      }
    },
    prepareAndSaveReconciliationAttachment(attach) {
      const name = attach.AttachName;
      const category = 1689;
      const extension = attach.AttachName.includes('.') ? '.' + attach.AttachName.split('.')
        .pop() : '.pdf';
      const fileType = extension === '.pdf' ? attachmentType.pdf : attachmentType.image;
      const attachment = {
        fileName: name,
        fileType,
        fileCategory: category,
        extension,
        sharePointId: attach.DocumentID,
        isAttachmentAudited: true,
      };
      return this.handleAttachments({
        attachments: [attachment],
        fileCategory: attachment.fileCategory,
      });
    },
    validateReconciliationRequestCode(silent = false) {
      const caseIsPersonalLawsuit = this.isPersonalLawsuit(this.caseData);
      if (this.caseData.reconciliationRequestCode && (this.firstProsecutorIdentityNumber || this.firstProsecutorCommercialRegistrationNumber || this.firstProsecutorUnifiedNumber)) {
        this.isCheckingReconciliationRequestCode = true;
        this.validateReconciliationCode({
          reconciliationRequestCode: this.caseData.reconciliationRequestCode,
          socialId: this.firstProsecutorIdentityNumber,
          commercialRegistrationNumber: this.firstProsecutorCommercialRegistrationNumber,
          unifiedNumber: this.firstProsecutorUnifiedNumber,
        })
          .then((res) => {
            switch (res.data.reconciliation.statusCode) {
              case reconciliationResultEnum.reconciliationSuccessfully:
                if (caseIsPersonalLawsuit)
                  this.$set(this.caseData, 'reconciliation', res.data.reconciliation);
                else
                  this.toaster.error(this.local.lawsuitRequest.reconciliationInfo.reconciliationSuccessfully);
                break;
              case reconciliationResultEnum.reconciliationInProgress:
              case reconciliationResultEnum.reconciliationInAudit:
                if (!silent)
                  this.toaster.error(this.local.lawsuitRequest.reconciliationInfo.reconciliationUnderProcessingStatusResult);
                break;
              default:
                if (!silent && !(res.data.reconciliation && res.data.reconciliation.attachmentList && res.data.reconciliation.attachmentList.length > 0))
                  this.toaster.success(this.local.common.verificationSucceeded);
                this.$set(this.caseData, 'reconciliation', res.data.reconciliation);
                if (res.data.reconciliation && res.data.reconciliation.attachmentList && res.data.reconciliation.attachmentList.length > 0 && !silent) {
                  const attachment = { ...res.data.reconciliation.attachmentList[0] };
                  this.prepareAndSaveReconciliationAttachment(attachment)
                    .then((res) => {
                      const msg = res ? this.local.lawsuitRequest.reconciliationInfo.verificationSucceededUploadSucceeded :
                        this.local.lawsuitRequest.reconciliationInfo.verificationSucceededUploadFailed;
                      this.toaster.success(msg);
                    });
                }
            }
          })
          .catch((err) => {
            if (!silent)
              this.toaster.error(err.response.data.detail);
          })
          .finally(() => {
            this.isCheckingReconciliationRequestCode = false;
          });
      } else {
        this.$set(this.caseData, 'reconciliation', {});
      }
    },
    onCourtChange() {
      this.formChanged('court-details');
      this.resetRelatedCases();
    },
  },
};
</script>
